/**
 * useIdleLogout — auto sign-out after sustained inactivity on the
 * authenticated app surface.
 *
 * Origin: real-user feedback (Beno, 2026-07-15) — "if someone is not active
 * on the site for 10 min, the site should log out automatically." Product
 * call updated 2026-07-20 (Paul): 45 minutes total, with a 60-second warning
 * at the 44-minute mark. 15 min was too aggressive for the document-heavy
 * fiduciary flows (long reads, multi-page onboarding, bulk client work); 45
 * + a warning is the revised middle ground.
 *
 * Timeline, measured from the last recorded activity:
 *
 *   0 ──────────────── 44 min ──────────────── 45 min
 *   active              WARNING shown           signed out
 *
 * Activity = pointerdown / keydown / wheel / touchstart anywhere in the
 * window (passive listeners — never blocks the real handler). Any of these,
 * or the warning dialog's "Stay signed in" action (`stayActive`), resets the
 * clock back to zero.
 *
 * Multi-tab sync: activity in ANY tab keeps every tab alive.
 *   - Each tab writes its last-activity timestamp to localStorage, throttled
 *     to ~1 write per 5 s so a busy tab doesn't hammer storage.
 *   - A live `storage` event fired by another tab's write resets THIS tab's
 *     clock immediately (storage events never fire in the tab that wrote
 *     them, only in the others — which is exactly the cross-tab signal we
 *     want).
 *   - Every scheduled check ALSO re-reads localStorage before acting on the
 *     warning/logout threshold ("read on check"), so a slow/late `storage`
 *     event, or a backgrounded tab whose timers the browser throttled,
 *     still can't log a tab out while another tab is genuinely active.
 *
 * Single-timer design: at most one setTimeout is ever in flight (no
 * interval, no polling loop). `scheduleNextCheck` computes the next
 * milestone (warn vs. logout) off the freshest known activity timestamp and
 * reschedules itself — it is the only place that calls setTimeout.
 *
 * Sign-out path: delegates to the shared `signOutAndRedirect` helper
 * (lib/sign-out.ts) — act-as/impersonation context cleared, then
 * `supabase.auth.signOut()` (errors swallowed — a failed network call must
 * not trap the user signed in), then a redirect to /auth/login. The same
 * helper backs the manual sign-outs in components/screens/screen-shell.tsx,
 * routes/app/profile-screen.tsx and routes/app/security.tsx; this hook only
 * adds the once-only guard so overlapping timers can't double-fire it.
 *
 * SSR guard: this is a Vite SPA (no SSR runtime), but every window /
 * localStorage touch is guarded so the module stays hermetic under vitest's
 * jsdom collection and any future non-DOM environment.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { signOutAndRedirect } from './sign-out';

/** Minutes of inactivity before the warning dialog appears. */
export const IDLE_WARNING_MS = 44 * 60 * 1000;
/** Minutes of inactivity before the session is force-signed-out. */
export const IDLE_LOGOUT_MS = 45 * 60 * 1000;
/** How long the warning stays up before logout — 60 s by product decision. */
export const IDLE_WARNING_DURATION_MS = IDLE_LOGOUT_MS - IDLE_WARNING_MS;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
const ACTIVITY_STORAGE_KEY = 'elreign:last-activity-at';
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function readStoredActivity(): number | null {
  if (!hasWindow()) return null;
  try {
    const raw = window.localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    // Private-mode / quota-exceeded localStorage — the in-memory timer
    // still works fine for THIS tab; multi-tab sync just doesn't apply.
    return null;
  }
}

function writeStoredActivity(ts: number): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts));
  } catch {
    /* see readStoredActivity — non-fatal */
  }
}

export interface UseIdleLogoutResult {
  /** True while the "you're about to be signed out" dialog should show. */
  warningOpen: boolean;
  /** Call from the "Stay signed in" affordance (or any explicit keep-alive
   *  action) to reset the idle clock immediately, bypassing the write
   *  throttle so other tabs see the reset right away. */
  stayActive: () => void;
}

/**
 * @param enabled Gate the whole mechanism. Hooks can't be called
 *   conditionally, so a consumer that only wants idle-tracking while a
 *   session exists passes `enabled={false}` rather than skipping the call.
 *   Defaults to `true` (matches mounting this only inside the authenticated
 *   app shell, once already past its session/redirect gates).
 */
export function useIdleLogout(enabled: boolean = true): UseIdleLogoutResult {
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const lastWriteRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signedOutRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const idleSignOut = useCallback(async () => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    await signOutAndRedirect(navigate);
  }, [navigate]);

  const scheduleNextCheck = useCallback(() => {
    clearTimer();
    if (!enabled || signedOutRef.current) return;

    // "Read on check" — pick up a fresher timestamp another tab wrote, even
    // if its `storage` event was missed or arrived late.
    const stored = readStoredActivity();
    if (stored !== null && stored > lastActivityRef.current) {
      lastActivityRef.current = stored;
    }

    const elapsed = Date.now() - lastActivityRef.current;

    if (elapsed >= IDLE_LOGOUT_MS) {
      setWarningOpen(false);
      void idleSignOut();
      return;
    }

    if (elapsed >= IDLE_WARNING_MS) {
      setWarningOpen(true);
      timerRef.current = setTimeout(scheduleNextCheck, IDLE_LOGOUT_MS - elapsed);
      return;
    }

    setWarningOpen(false);
    timerRef.current = setTimeout(scheduleNextCheck, IDLE_WARNING_MS - elapsed);
  }, [enabled, clearTimer, idleSignOut]);

  const recordActivity = useCallback((forceWrite: boolean) => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (forceWrite || now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeStoredActivity(now);
    }
    scheduleNextCheck();
  }, [scheduleNextCheck]);

  const stayActive = useCallback(() => {
    recordActivity(true);
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled || !hasWindow()) {
      clearTimer();
      setWarningOpen(false);
      return;
    }

    signedOutRef.current = false;
    lastActivityRef.current = Date.now();
    scheduleNextCheck();

    const handleActivity = () => recordActivity(false);

    const handleStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_STORAGE_KEY || e.newValue == null) return;
      const ts = Number(e.newValue);
      if (Number.isFinite(ts) && ts > lastActivityRef.current) {
        lastActivityRef.current = ts;
        scheduleNextCheck();
      }
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, handleActivity, { passive: true });
    }
    window.addEventListener('storage', handleStorage);

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, handleActivity);
      }
      window.removeEventListener('storage', handleStorage);
      clearTimer();
    };
  }, [enabled, scheduleNextCheck, recordActivity, clearTimer]);

  return { warningOpen, stayActive };
}
